/** Angular imports */
import {
    Component,
    computed,
    effect,
    inject,
    input,
    output,
    signal,
    viewChild,
} from '@angular/core';
import { catchError, EMPTY } from 'rxjs';

/** Component imports */
import { SelectAssetsComponent } from '@features/playlists/components/ui';
import { Button, Modal, Stepper, StepperConfig } from '@ntv360/component-pantry';
import { ConfirmationModalComponent } from '@shared/components/modals/confirmation-modal/confirmation-modal.component';
import { PreviewComponent, SettingsComponent } from './steps';

/** Local imports */
import { AddContentStepsService, PlaylistsService } from '@core';
import { ADD_CONTENT_STEPS } from '@features/playlists/constants';
import { SUB_STEPPER_CONFIG } from '@shared/constants';
import { Logger, withLoading } from '@shared/utils';

/** Steps components */
const stepsComponent = [SelectAssetsComponent, PreviewComponent, SettingsComponent];

@Component({
    selector: 'app-add-content',
    imports: [Modal, Button, Stepper, ConfirmationModalComponent, ...stepsComponent],
    templateUrl: './add-content.component.html',
    styleUrl: './add-content.component.scss',
})
export class AddContentComponent {
    /** Injected add content steps for steps validation */
    private addContentStepsService = inject(AddContentStepsService);

    /** Injected playlists service to assign content */
    private playlistsService = inject(PlaylistsService);

    /** Add content steps */
    public addContentSteps = ADD_CONTENT_STEPS;

    /** Component reference to the select assets component */
    public selectAssetsComponent = viewChild(SelectAssetsComponent);

    /** Component reference to the settings component */
    public settingsComponent = viewChild(SettingsComponent);

    /** Controls the visibility of add content modal */
    public showAddContentModal = input.required<boolean>();

    /** Selected content type */
    public selectedContentType = input.required<'single' | 'block'>();

    /** The playlist Id to assign contents to */
    public playlistId = input.required<string>();

    /** Dealer ID of the playlist owner; forwarded to select-assets so admins fetch dealer-scoped contents alongside floating. */
    public dealerId = input<string | null>(null);

    /** Holds the current step index */
    public currentStepIndex = signal<number>(0);

    /** True while the assign contents API request is in flight */
    protected isSaving = signal<boolean>(false);

    /** Controls the visibility of the success/error confirmation modal */
    protected showResultModal = signal<boolean>(false);

    /** Configuration for the result modal */
    protected resultModalConfig = signal<{
        type: 'success' | 'error';
        title: string;
        message: string;
    }>({
        type: 'success',
        title: '',
        message: '',
    });

    constructor() {
        // Sync local current step index to the service
        effect(() => {
            this.addContentStepsService.setCurrentStepIdx(this.currentStepIndex());
        });
    }

    /** Emits when the modal is closed */
    public modalClose = output<void>();

    /** Emits when contents have been successfully added to refresh the playlist */
    public contentAdded = output<void>();

    /** Whether the current step is valid or not */
    protected isCurrentStepValid = this.addContentStepsService.isStepValid;

    /**
     * Computes the stepper configuration while disabling clicks during draft save/load operations.
     * Also hides descriptions to maintain a cleaner stepper UI.
     */
    public stepperConfig = computed(
        (): StepperConfig => ({
            ...SUB_STEPPER_CONFIG,
            clickable: true,
            showDescriptions: false,
            size: 'md',
        }),
    );

    /**
     * Handles the next step click event
     */
    protected onNextStep(): void {
        const nextStep = this.currentStepIndex() + 1;
        if (this.currentStepIndex() === 0) {
            this.selectAssetsComponent()?.saveDataToDraft();
        }
        if (nextStep < this.addContentSteps.length) {
            this.currentStepIndex.set(nextStep);
        } else {
            // Last step, save the settings first and then submit the data
            this.settingsComponent()?.saveDataToDraft();
            this.submitContents();
        }
    }

    /**
     * Submits the selected contents to the playlist contents assignment API
     */
    private submitContents(): void {
        const playlistId = this.playlistId();
        const draft = this.addContentStepsService.getDraft();
        const selectedItems = draft.selectedContents?.selectedItems || [];

        if (!playlistId || selectedItems.length === 0) {
            this.modalClose.emit();
            return;
        }

        // Format payload, fixing contentType to 'media' as requested
        // Coerce to number: pantry's ntv-input writes the raw DOM string back
        // through the FormControl<number> despite its type.
        const duration = Number(draft.settings?.duration ?? 0);
        const payload = {
            contentIds: selectedItems.map((item) => ({
                id: item.contentId || item.id,
                contentType: 'media' as const,
                duration,
            })),
        };

        this.playlistsService
            .assignContentsToPlaylist(playlistId, payload)
            .pipe(
                withLoading(this.isSaving),
                catchError((err: unknown) => {
                    Logger.error(
                        'AddContentComponent',
                        'Failed to assign contents to playlist',
                        err,
                    );
                    this.resultModalConfig.set({
                        type: 'error',
                        title: 'Failed to Add Content',
                        message:
                            'An error occurred while adding contents to the playlist. Please try again.',
                    });
                    this.showResultModal.set(true);
                    return EMPTY;
                }),
            )
            .subscribe({
                next: () => {
                    this.resultModalConfig.set({
                        type: 'success',
                        title: 'Content Added',
                        message:
                            'The selected contents have been successfully added to this playlist.',
                    });
                    this.showResultModal.set(true);
                },
            });
    }

    /**
     * Closes the success/error result modal and propagates success event
     */
    protected onResultModalClose(): void {
        const isSuccess = this.resultModalConfig().type === 'success';
        this.showResultModal.set(false);
        if (isSuccess) {
            this.contentAdded.emit();
            this.onClose();
        }
    }

    /**
     * Closes the modal and clears the draft so any selected contents do not persist
     * the next time the modal is reopened.
     */
    protected onClose(): void {
        this.addContentStepsService.clearDraft();
        this.modalClose.emit();
    }

    /**
     * Handles the previous step click event
     */
    protected onPreviousStep(): void {
        const prevStep = this.currentStepIndex() - 1;
        if (prevStep >= 0) {
            this.currentStepIndex.set(prevStep);
        }
    }
}
